import React, { useState, useCallback, useEffect, useMemo, memo } from 'react';
import { useDispatch } from 'react-redux';
import { GlobalHotKeys } from 'react-hotkeys';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

import { jsonEmptyContentState, jsonContentStateFromString } from 'Shared/utils/draftJsHelpers';
import { updateSortedActionItemUuids } from 'Shared/actions/sortedActionItemsUuids';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { addItem, nestTodo, addSubItem } from 'Shared/actions/todos';
import { getItemsForStandardView } from 'Shared/selectors/workspace';
import AddItemButtons from 'Shared/components/AddItemButtons/AddItemButtons';
import { ItemDragLayer } from 'Shared/components/DragAndDropComponents';
import { useDeepEqualSelector, useDeepCompareMemoize } from 'Shared/utils/hooks';
import ActionItemsSortingHeader from 'Dashboard/components/ActionItemsSortingHeader/ActionItemsSortingHeader';
import { getSortingOption, getUuidsSortedItems } from 'Dashboard/components/ActionItemsSortingHeader/utils';
import { getSortedItems } from 'Shared/utils/sorting';
import { ItemType } from 'Shared/constants/types';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';

import WorkspaceItemsList from '../WorkspaceItemsList/WorkspaceItemsList';
import VideoRecorder from 'Shared/components/VideoRecorder/VideoRecorder';

import './ActionItems.scss';

// Hooks
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useApp } from 'Shared/utils/hooks/use-app';

const keyMap = {
  ADD_ITEM: ['ctrl+shift+i', 'command+shift+i'],
  ADD_SECTION: ['ctrl+shift+s', 'command+shift+s'],
};

type Props = {
  isMobile?: boolean;
  actionItemsTodosContentRef: {
    current: HTMLDivElement;
  };
  className?: string;
  onFocusedTodoUpdate: Function;
};

function ActionItems({ isMobile, actionItemsTodosContentRef, className = '', onFocusedTodoUpdate }: Props) {
  const dispatch = useDispatch();
  const { isZoom } = useApp();
  const { isTeamSync, isOneOnOne, isPersonal: isPersonalWorkspace, id: workspaceId } = useWorkspace();

  const sortedActionItemsUuids = useDeepEqualSelector(state => state.sortedActionItemsUuids);

  const [filters, setFilters] = useState({ showGoalsItems: true, workspaceId });
  const [selectedSort, setSelectedSort] = useState({
    actionItems: 'default',
    owner: 'default',
    dueDate: 'default',
  });
  const actionItems = useDeepEqualSelector(state => [
    ...getSortedItems({
      items: getItemsForStandardView(state.workspace.meeting_items),
      selectedSort,
      withHighPriority: true,
      positionField: 'position',
    })
      .filter(item => item.workspaceId === filters.workspaceId && (filters.showGoalsItems || !item.goalSlug))
      .map(({ uuid, parentUuid, isHighPriority }) => ({ uuid, parentUuid, isHighPriority })),
  ]);
  const [isShowVideoRecorder, setIsShowVideoRecorder] = useState(false);

  let emptyStateMessage = 'Keep the team on track by creating action items during, in between and after meetings.';
  if (isOneOnOne) {
    emptyStateMessage = 'Keep each other accountable by creating action items during, in between and after meetings.';
  } else if (isPersonalWorkspace) {
    emptyStateMessage = 'Stay on top of your tasks by creating action items for yourself.';
  }

  const handleUpdateSortedActionItemsItemUuids = useCallback(
    () => dispatch(updateSortedActionItemUuids(getUuidsSortedItems(actionItems))),
    [dispatch, actionItems]
  );

  useEffect(() => {
    handleUpdateSortedActionItemsItemUuids();
  }, [useDeepCompareMemoize(actionItems)]);

  const handleSelectedItemUpdate = useCallback(
    ({ itemUuid }) =>
      setTimeout(() => {
        onFocusedTodoUpdate(itemUuid);
        dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true }));
      }, 0),
    [onFocusedTodoUpdate, dispatch]
  );

  const handleAddMeetingItem = useCallback(
    ({ jsonBody, asanaData, servicenowData, dueBy, assignmentInfo, isAssignCurrentUser } = {}) => {
      const defaultDueBy = moment(new Date()).startOf('day').startOf('hour').add(7, 'days').add(12, 'hours').unix();
      const body = jsonBody || jsonEmptyContentState();
      const itemDueBy = dueBy === undefined ? defaultDueBy : dueBy;
      const itemUuid = uuidv4();

      dispatch(
        addItem({
          itemUuid,
          body,
          workspaceId,
          position: isMobile ? actionItems.length + 1 : 1,
          dueBy: itemDueBy,
          asanaData,
          servicenowData,
          assignmentInfo,
          isAssignCurrentUser,
        })
      );
      // Focus on the first element, if it exists.
      handleSelectedItemUpdate({ itemUuid });
    },
    [dispatch, workspaceId, handleSelectedItemUpdate, actionItems.length, isMobile]
  );

  const handleAddSubItem = useCallback(() => {
    const body = jsonEmptyContentState();
    const itemUuid = uuidv4();
    dispatch(addSubItem({ itemUuid, body, workspaceId, itemType: ItemType.actionItem }));

    handleSelectedItemUpdate({ itemUuid });
  }, [dispatch, workspaceId, handleSelectedItemUpdate]);

  const handleClickSortOption = useCallback(
    event => {
      const selectedIdSort = event.currentTarget.id;
      setSelectedSort({
        actionItems: selectedIdSort === 'actionItems' ? getSortingOption(selectedSort.actionItems) : 'default',
        owner: selectedIdSort === 'owner' ? getSortingOption(selectedSort.owner) : 'default',
        dueDate: selectedIdSort === 'dueDate' ? getSortingOption(selectedSort.dueDate) : 'default',
      });
    },
    [selectedSort]
  );

  const handlers = useMemo(
    () => ({
      ADD_ITEM: event => {
        event.preventDefault();
        handleAddMeetingItem();
      },
    }),
    [handleAddMeetingItem]
  );

  const handleArrowUpClick = useCallback(
    ({ uuid: currentItemUuid }) => {
      const curentItemIndex = sortedActionItemsUuids.indexOf(currentItemUuid);
      const prevItemUuid = sortedActionItemsUuids[curentItemIndex - 1];
      if (prevItemUuid) handleSelectedItemUpdate({ itemUuid: prevItemUuid });
    },
    [sortedActionItemsUuids, handleSelectedItemUpdate]
  );

  const handleArrowDownClick = useCallback(
    ({ uuid: currentItemUuid }) => {
      const curentItemIndex = sortedActionItemsUuids.indexOf(currentItemUuid);
      const nextItemUuid = sortedActionItemsUuids[curentItemIndex + 1];
      if (nextItemUuid) handleSelectedItemUpdate({ itemUuid: nextItemUuid });
    },
    [sortedActionItemsUuids, handleSelectedItemUpdate]
  );

  const handleNestingByDrag = useCallback(
    ({ draggedItemUuids, hoveredItemUuid, hoveredItemId }) => {
      dispatch(
        nestTodo({
          draggedItemUuids,
          parentId: hoveredItemId,
          parentUuid: hoveredItemUuid,
          itemType: ItemType.actionItem,
          workspaceId,
          byDrag: true,
        })
      );
    },
    [dispatch, workspaceId]
  );

  const handleVideoItemAdd = useCallback(
    video => {
      const body = jsonContentStateFromString(video.audioOnly ? 'New Audio Item' : 'New Video Item');
      const itemUuid = uuidv4();
      dispatch(addItem({ itemUuid, body, workspaceId, video, position: 1 }));
    },
    [dispatch, workspaceId]
  );

  const handleVideoRecorderToggle = useCallback(() => setIsShowVideoRecorder(!isShowVideoRecorder), [
    isShowVideoRecorder,
  ]);

  const handleShowGoalsActionItemsToggle = useCallback(() => {
    (window as any).analytics.track('Filtered Action Items', {
      filterType: !filters.showGoalsItems ? 'show_goals' : 'hide_goals',
    });
    setFilters(state => ({ ...filters, showGoalsItems: !state.showGoalsItems }));
  }, [filters]);

  const forbidDrag = useMemo(
    () => Boolean(Object.keys(selectedSort).find(sortId => selectedSort[sortId] !== 'default')),
    [selectedSort]
  );

  const showEmptyState = !isZoom && actionItems.length === 0;

  return (
    <GlobalHotKeys keyMap={keyMap} handlers={handlers}>
      <ItemDragLayer />
      <div className={`action-items ${className}`}>
        <div className="d-sm-flex w-100 mb-3 mb-sm-4 mt-5 align-items-center justify-content-between">
          <h4 className="action-items-title m-0">Action Items</h4>
          {!isMobile && (
            <div className="action-items__header-btns d-flex justify-content-end mt-2 mt-sm-0">
              <WpButtonGroup>
                {isTeamSync && (
                  <WpButton variant="tertiary" onClick={handleShowGoalsActionItemsToggle}>
                    {filters.showGoalsItems ? 'Hide' : 'Show'} Goal Action Items
                  </WpButton>
                )}
                <AddItemButtons
                  onAddMeetingItem={handleAddMeetingItem}
                  onAddSubItem={handleAddSubItem}
                  onVideoRecorderToggle={handleVideoRecorderToggle}
                />
              </WpButtonGroup>
            </div>
          )}
        </div>
        {showEmptyState && (
          <EmptyStateContainer
            subTitle={emptyStateMessage}
            button={
              <WpButton variant="tertiary" onClick={handleAddMeetingItem}>
                <WpIcon name="plus" />
                Add Action Item
              </WpButton>
            }
            inContainer
          />
        )}
        {!showEmptyState && (
          <div className="action-items-content mb-3" ref={actionItemsTodosContentRef}>
            <ActionItemsSortingHeader
              withDueDateSection
              withOwnerSection
              selectedSort={selectedSort}
              onClickSortOption={handleClickSortOption}
            >
              <WorkspaceItemsList
                forbidDrag={forbidDrag}
                listOfParentUuid={null}
                items={actionItems}
                listType={ItemType.actionItem}
                onNestByDrag={handleNestingByDrag}
                onArrowUpClick={handleArrowUpClick}
                onArrowDownClick={handleArrowDownClick}
              />
            </ActionItemsSortingHeader>
          </div>
        )}

        {isMobile && (
          <div className="action-items__add-item-buttons">
            <WpButton variant="tertiary" onClick={handleAddMeetingItem}>
              <WpIcon name="plus-circle" />
              Add Action Item
            </WpButton>
          </div>
        )}

        {isShowVideoRecorder && <VideoRecorder onSave={handleVideoItemAdd} onHide={handleVideoRecorderToggle} />}
      </div>
    </GlobalHotKeys>
  );
}

export default memo(ActionItems);
